// The "My budget" card inside "Where can I afford to buy?": income, down payment, rate quote and term → the personal
// affordability metrics. Other places (an area's affordability line, the Screener's budget idea) open it with edit().
import { describeBudget, readBudget, type Budget } from "./budget.ts";
import { el, nodes } from "./dom.ts";

export interface BudgetDeps {
  get(): Budget | null;
  /** save (null = clear) and recolor the map; `show` = also switch the map to "My Payment as % of My Income" */
  set(b: Budget | null, show: boolean): void;
  /** the latest average 30-year rate, the rate field's default */
  latestRate: number | null;
}

export function budgetBox(d: BudgetDeps) {
  const root = el("section", { class: "budget", "aria-label": "My budget" });
  let editing = false;

  const render = () => {
    const b = d.get();
    if (!editing) {
      root.replaceChildren(
        el("div", { class: "budget-row" },
          el("div", {},
            el("strong", {}, "My budget"),
            el("div", { class: "muted small" }, b ? describeBudget(b, d.latestRate) : "Add your income to see what homes cost you, not the typical household")),
          el("div", { class: "budget-actions" },
            b ? el("button", { class: "btn small", onclick: () => d.set(b, true) }, "Map it") : null,
            el("button", { class: "btn small", onclick: () => { editing = true; render(); } }, b ? "Edit" : "Set"))));
      return;
    }
    const income = el("input", { type: "text", inputmode: "decimal", placeholder: "e.g. 120k", "aria-label": "Yearly household income",
      value: b ? String(b.income) : "" });
    const down = el("input", { type: "text", placeholder: "60k or 20%", "aria-label": "Down payment",
      value: b ? ("usd" in b.down ? String(b.down.usd) : `${Math.round(b.down.share * 1000) / 10}%`) : "" });
    const rate = el("input", { type: "text", inputmode: "decimal", "aria-label": "Mortgage rate (%)",
      placeholder: d.latestRate != null ? `${d.latestRate.toFixed(2)} (avg.)` : "avg.", value: b?.rate != null ? String(b.rate) : "" });
    const term = el("select", { "aria-label": "Loan term" },
      ...[30, 20, 15].map((y) => el("option", { value: String(y), selected: (b?.termYears ?? 30) === y }, `${y} years`)));
    const err = el("div", { class: "budget-error small", role: "alert" });
    const save = () => {
      const r = readBudget({ income: income.value, down: down.value, rate: rate.value, term: term.value });
      if (typeof r === "string") { err.textContent = r; return; }
      editing = false;
      d.set(r, true);
    };
    for (const i of [income, down, rate]) i.addEventListener("keydown", (e) => { if (e.key === "Enter") save(); });
    root.replaceChildren(...nodes(
      el("strong", {}, "My budget"),
      el("label", { class: "budget-field" }, el("span", {}, "Yearly household income"), income),
      el("label", { class: "budget-field" }, el("span", {}, "Down payment"), down),
      el("div", { class: "budget-pair" },
        el("label", { class: "budget-field" }, el("span", {}, "Rate %"), rate),
        el("label", { class: "budget-field" }, el("span", {}, "Term"), term)),
      err,
      el("p", { class: "muted small" }, "Kept in this browser only, never in links you share. Blank rate = each month's average 30-year rate; blank down payment = 20%."),
      el("div", { class: "budget-actions" },
        el("button", { class: "btn small primary", onclick: save }, "Show on map"),
        el("button", { class: "btn small", onclick: () => { editing = false; render(); } }, "Cancel"),
        b ? el("button", { class: "btn small ghost", onclick: () => { editing = false; d.set(null, false); } }, "Clear") : null)));
    queueMicrotask(() => income.focus());
  };
  render();
  /** open the form (from another part of the app) */
  const edit = () => { editing = true; render(); root.scrollIntoView({ block: "nearest" }); };
  return { el: root, render, edit };
}
